import React from 'react';

import { useQuery } from '@apollo/client';
import { Box, Shimmer, VStack } from '@betterment/samba-react';
import {
  AccountSettingsModalContent,
  type CashAccount,
  type DirectTradingAccount,
  type ManagedAccount,
} from '@betterment/shared-components';
import { useParams } from 'react-router-dom';

import { AccountSettingsModalDocument } from './graphql/query.accountSettingsModal.generated';

import { CLIENT_TYPE_NAME, TRUST_TYPE_NAME } from '../../Shared/constants';
import urlBuilder from '../../Shared/url-builder';
import FormButton from '../FormButton';
import { ImpersonationLink } from '../Household/ImpersonationLink';

type ClientAccountSettingsModalContentProps = {
  accountId: string;
  currentPath: string;
};

export const ClientAccountSettingsModalContent: React.FC<ClientAccountSettingsModalContentProps> = ({
  accountId,
  currentPath,
}) => {
  const { data, loading, error } = useQuery(AccountSettingsModalDocument, {
    variables: { id: accountId },
  });

  const { householdId } = useParams<{ householdId: string }>();

  if (loading) {
    return (
      <VStack css={{ gap: 'space.200', p: 'space.300' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Shimmer css={{ height: 'size.400', width: '40%' }} />
          <Shimmer css={{ height: 'size.400', width: '60%' }} />
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Shimmer css={{ height: 'size.400', width: '40%' }} />
          <Shimmer css={{ height: 'size.400', width: '60%' }} />
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Shimmer css={{ height: 'size.400', width: '40%' }} />
          <Shimmer css={{ height: 'size.400', width: '60%' }} />
        </Box>
      </VStack>
    );
  }

  if (error) return <div>Error loading account settings</div>;
  if (!data?.envelopeAccount) return <div>Account not found</div>;

  const account = data.envelopeAccount;

  const purposeId = account.envelope.purpose?.id;
  const deepLinkPath = purposeId ? `/app/purpose/${purposeId}` : `/app/account/${account.id}`;

  const clientId = account.users[0]?.id || '';

  const firstAccountHolder = account.accountHolders[0];
  let clientName: string | undefined;
  if (firstAccountHolder?.__typename === CLIENT_TYPE_NAME) {
    clientName = `${firstAccountHolder.commonFirstName} ${firstAccountHolder.lastName}`;
  } else if (firstAccountHolder?.__typename === TRUST_TYPE_NAME) {
    clientName = firstAccountHolder.name;
  }

  const legalAccountsUrl = urlBuilder.getImpersonateClientPage.absolute({
    clientId,
    deep_link_path: deepLinkPath,
  });

  const mapEnvelope = () => ({
    id: account.envelope.id,
    purpose: account.envelope.purpose
      ? {
          __typename: account.envelope.purpose.__typename,
          id: account.envelope.purpose.id,
          name: account.envelope.purpose.name,
        }
      : null,
  });

  const accountSettingsUrlBuilders = {
    accountDetails: (
      <ImpersonationLink css={{ color: 'text.link' }} label="Edit account" url={legalAccountsUrl} showModal={false} />
    ),
    editPurpose: (
      <ImpersonationLink css={{ color: 'text.link' }} label="Add to a goal" url={legalAccountsUrl} showModal={false} />
    ),
    editPortfolio:
      account.__typename === 'ManagedAccount' ? (
        <FormButton
          method="post"
          action={urlBuilder.getEditPortfolioPage.absolute({
            goal_id: account.legacyGoalId,
            return_to_path: urlBuilder.getHouseholdSummaryPage.absolute({ householdId: householdId! }),
          })}
          variant="textSecondary"
          css={{ textDecoration: 'none', color: 'text.link' }}
        >
          Edit portfolio
        </FormButton>
      ) : undefined,
  };

  const purposelessCopy =
    'Assign this account to a goal to track progress and help your clients reach their savings target.';

  if (account.__typename === 'ManagedAccount') {
    const portfolio: ManagedAccount['portfolio'] =
      account.portfolio.__typename === 'RiskLevelBasedPortfolio'
        ? {
            __typename: 'RiskLevelBasedPortfolio',
            strategy: { name: account.portfolio.strategyName },
            riskLevel: { name: account.portfolio.targetAllocationDescription },
            isAllocationEditable: true,
            isStrategySelectionEligible: account.portfolio.isStrategySelectionEligible,
            targetAllocationDescription: account.portfolio.targetAllocationDescription,
          }
        : {
            __typename: 'SelfDirectedPortfolio',
            name: account.portfolio.strategyName,
            isAllocationEditable: true,
            isStrategySelectionEligible: account.portfolio.isStrategySelectionEligible,
            targetAllocationDescription: account.portfolio.targetAllocationDescription,
          };

    const mappedAccount: ManagedAccount = {
      __typename: 'ManagedAccount',
      id: account.id,
      name: account.name,
      balance: account.balance,
      isTaxCoordinated: account.isTaxCoordinated,
      isTaxCoordinationEligible: false,
      legacyGoalId: parseInt(account.legacyGoalId, 10),
      legacyId: account.legacyId,
      autoglide: {
        isEnabled: account.autoglide.isEnabled,
        isEligible: true,
      },
      portfolio,
      subAccounts: account.subAccounts.map(sub => ({
        legacySubAccountId: sub.legacySubAccountId,
        legalAccount: {
          ownershipType: sub.legalAccount.ownershipType,
          taxationType: sub.legalAccount.taxationType,
          primaryBeneficiaries: [],
        },
      })),
      envelope: mapEnvelope(),
    };

    return (
      <AccountSettingsModalContent
        account={mappedAccount}
        currentPath={currentPath}
        urlBuilders={accountSettingsUrlBuilders}
        clientName={clientName}
        showInlineEditLinks={false}
        purposelessCopy={purposelessCopy}
      />
    );
  }

  // Handle CashAccount
  if (account.__typename === 'CashAccount') {
    const cashAccount: CashAccount = {
      __typename: 'CashAccount',
      id: account.id,
      name: account.name,
      balance: account.balance,
      legacySubAccountId: account.legacySubAccountId,
      legacyGoalId: account.cashAccountLegacyGoalId,
      legalAccount: {
        ownershipType: account.legalAccount.ownershipType,
        taxationType: account.legalAccount.taxationType,
        primaryBeneficiaries: [],
      },
      envelope: mapEnvelope(),
    };

    return (
      <AccountSettingsModalContent
        account={cashAccount}
        currentPath={currentPath}
        urlBuilders={accountSettingsUrlBuilders}
        showInlineEditLinks={false}
        purposelessCopy={purposelessCopy}
      />
    );
  }

  // Handle DirectTradingAccount
  if (account.__typename === 'DirectTradingAccount') {
    const directTradingAccount: DirectTradingAccount = {
      __typename: 'DirectTradingAccount',
      id: account.id,
      name: account.name,
      balance: account.balance,
      legacySubAccountId: account.legacySubAccountId,
      legalAccount: {
        ownershipType: account.legalAccount.ownershipType,
        taxationType: account.legalAccount.taxationType,
        primaryBeneficiaries: [],
      },
      envelope: mapEnvelope(),
    };

    return (
      <AccountSettingsModalContent
        account={directTradingAccount}
        currentPath={currentPath}
        urlBuilders={accountSettingsUrlBuilders}
        showInlineEditLinks={false}
      />
    );
  }

  return <div>Account type not supported</div>;
};
